import React, { useState, useEffect } from 'react';
import {
    Dialog, DialogTitle, DialogContent,
    List, ListItem, ListItemText, ListItemAvatar,
    Avatar, IconButton, Typography, Badge,
    Menu, MenuItem, Divider, Box, Button
} from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import CloseIcon from '@mui/icons-material/Close';
import axios from 'axios';
import { formatDistanceToNow } from 'date-fns';
import DoneAllIcon from '@mui/icons-material/DoneAll';

const NotificationCenter = ({ isDarkMode, customClass }) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const open = Boolean(anchorEl);

    const fetchNotifications = async () => {
        try {
            const response = await axios.get('https://jutiyapa.com/notifications/');
            // Assumption: API returns array of objects with { id, title, message, created_at, is_read }
            if (Array.isArray(response.data)) {
                // Mapping API fields to internal state
                const mappedData = response.data.map(n => ({
                    id: n.notification_id,
                    type: n.notification_type,
                    title: `New ${n.notification_type} from ${n.user}`,
                    message: n.comment_text || n.post || "New interaction",
                    is_read: n.status === 'read',
                    created_at: n.created_at,
                    platform: n.platform,
                    post_id: n.post_id,
                    comment_id: n.comment_id
                }));
                setNotifications(mappedData);
                const unread = mappedData.filter(n => !n.is_read).length;
                setUnreadCount(unread);
            }
        } catch (error) {
            console.error("Error fetching notifications:", error);
        }
    };

    useEffect(() => {
        fetchNotifications();
        // Poll for notifications every 30 seconds
        const interval = setInterval(fetchNotifications, 30000);
        return () => clearInterval(interval);
    }, []);

    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
        fetchNotifications(); // Refresh on open
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const markAsRead = async (ids) => {
        try {
            await axios.post('https://jutiyapa.com/notifications/mark-read/', {
                notification_ids: ids
            });
            // Update local state
            // Optimistic Update
            setNotifications(prev => prev.map(n =>
                ids.includes(n.id) ? { ...n, is_read: true } : n
            ));
            setUnreadCount(prev => {
                // Calculate new unread count based on current set
                const unread = notifications.filter(n => !ids.includes(n.id) && !n.is_read).length;
                return Math.max(0, unread);
            });
        } catch (error) {
            console.error("Error marking read:", error);
            // Revert if needed, or just let next poll fix it
        }
    };

    const handleMarkAllRead = () => {
        const unreadIds = notifications.filter(n => !n.is_read).map(n => n.id);
        if (unreadIds.length > 0) {
            markAsRead(unreadIds);
        }
    };

    const handleNotificationClick = (notification) => {
        if (!notification.is_read) {
            markAsRead([notification.id]);
        }

        // Construct URL and open in new tab
        if (notification.post_id) {
            let url = `https://casualfootwears.com/posts/${notification.post_id}/`;
            if (notification.comment_id) {
                url += `#${notification.comment_id}`;
            }
            window.open(url, '_blank', 'noopener,noreferrer');
        }
    };

    // Styles based on theme
    const bgColor = isDarkMode ? '#202c33' : '#fff';
    const textColor = isDarkMode ? '#e9edef' : '#000';
    const hoverColor = isDarkMode ? '#111b21' : '#f5f5f5';
    const secondaryText = isDarkMode ? '#8696a0' : '#667781';

    return (
        <>
            {customClass ? (
                <div className={customClass} onClick={handleClick}>
                    <Badge badgeContent={unreadCount} color="error" sx={{ '& .MuiBadge-badge': { top: 5, right: 5 } }}>
                        <NotificationsIcon sx={{ fontSize: "24px" }} />
                    </Badge>
                </div>
            ) : (
                <IconButton onClick={handleClick} style={{ color: isDarkMode ? '#aebac1' : 'inherit' }}>
                    <Badge badgeContent={unreadCount} color="error">
                        <NotificationsIcon />
                    </Badge>
                </IconButton>
            )}

            <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                PaperProps={{
                    style: {
                        maxHeight: 400,
                        width: '350px',
                        backgroundColor: bgColor,
                        color: textColor,
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                        border: isDarkMode ? '1px solid #333' : 'none'
                    },
                }}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            >
                <Box display="flex" justifyContent="space-between" alignItems="center" p={2} borderBottom={`1px solid ${isDarkMode ? '#333' : '#eee'}`}>
                    <Typography variant="h6" style={{ fontSize: '1.1rem', fontWeight: 600 }}>
                        Notifications
                    </Typography>
                    {unreadCount > 0 && (
                        <Button
                            size="small"
                            startIcon={<DoneAllIcon />}
                            onClick={handleMarkAllRead}
                            style={{ textTransform: 'none', color: '#00a884' }}
                        >
                            Mark all read
                        </Button>
                    )}
                </Box>

                {notifications.length === 0 ? (
                    <Box p={3} textAlign="center" color={secondaryText}>
                        <Typography variant="body2">No notifications</Typography>
                    </Box>
                ) : (
                    <List style={{ padding: 0 }}>
                        {notifications.map((notification) => (
                            <ListItem
                                button
                                key={notification.id}
                                onClick={() => handleNotificationClick(notification)}
                                style={{
                                    backgroundColor: notification.is_read ? 'transparent' : (isDarkMode ? 'rgba(0, 168, 132, 0.1)' : '#eaffe9'),
                                    borderBottom: `1px solid ${isDarkMode ? '#2a3942' : '#f0f0f0'}`
                                }}
                            >
                                <ListItemAvatar>
                                    <Avatar sx={{ bgcolor: isDarkMode ? '#00a884' : '#25D366', width: 32, height: 32 }}>
                                        <NotificationsIcon style={{ fontSize: 18 }} />
                                    </Avatar>
                                </ListItemAvatar>
                                <ListItemText
                                    primary={
                                        <Typography variant="subtitle2" style={{ fontWeight: notification.is_read ? 400 : 600 }}>
                                            {notification.title || "Notification"}
                                        </Typography>
                                    }
                                    secondary={
                                        <>
                                            <Typography component="span" variant="body2" style={{ display: 'block', color: textColor, fontSize: '0.85rem' }}>
                                                {notification.message}
                                            </Typography>
                                            <Typography component="span" variant="caption" style={{ color: secondaryText, fontSize: '0.75rem' }}>
                                                {notification.created_at ? formatDistanceToNow(new Date(notification.created_at), { addSuffix: true }) : ''}
                                            </Typography>
                                        </>
                                    }
                                />
                                {!notification.is_read && (
                                    <Box
                                        sx={{
                                            width: 8,
                                            height: 8,
                                            borderRadius: '50%',
                                            bgcolor: '#00a884',
                                            marginLeft: 1
                                        }}
                                    />
                                )}
                            </ListItem>
                        ))}
                    </List>
                )}
            </Menu>
        </>
    );
};

export default NotificationCenter;
